"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ArrowRight, UserCheck, ShieldCheck, Sparkles, TrendingUp, Target, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const foundationPriorities = [{
  text: "Resolve critical technical blockers to ensure clean indexing and site speed"
}, {
  text: "Optimise highest-commercial-intent service and product landing pages"
}, {
  text: "Target high-converting search queries for rapid, measurable inquiry gains"
}];
const scalingPriorities = [{
  text: "Broaden scope into secondary keyword themes and supporting content"
}, {
  text: "Expand topical authority and build sustainable industry search signals"
}, {
  text: "Incrementally scale consultancy hours and strategic scope as revenue grows"
}];
export default function SmallBusinessFocus() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.21, 0.47, 0.32, 0.98]
      }
    }
  };
  return <section id="smallbusinessseofocus" data-nav="light" className="relative bg-background py-20 md:py-28 overflow-hidden">
      {/* Subtle decorative background glow */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-40">
        <div className="h-[450px] w-[600px] rounded-full bg-secondary/60 blur-3xl" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="space-y-16">
          {/* Header & Main Narrative */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <motion.div variants={itemVariants} className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2">
                <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary border-primary/20">
                  <UserCheck className="size-3.5 text-primary" /> Senior Expertise
                </Badge>
              </div>

              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground leading-[1.15]">
                Finally, professional SEO services built for UK small businesses
              </h2>

              <div className="space-y-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
                <p>
                  Securing high-impact SEO should not require £2,000–£5,000+ monthly retainers or paying for large administrative overheads. For many growing UK businesses, paying for multi-tier management structures and bundled services that do not align with their immediate needs is not cost-effective. Small businesses need focused, budget-conscious SEO where investment translates directly into qualified search visibility and commercial returns.
                </p>

                <p>
                  Small businesses and SMEs deserve better. Most do not need a 20-person agency team. They need a skilled, experienced SEO consultant who listens, understands their market, and builds a strategy that actually fits their goals and budget.
                </p>

                <p className="font-medium text-foreground">
                  That is the foundation of my consultancy. As an independent SEO consultant, I provide UK businesses with professional, affordable SEO services built on close collaboration and clear commercial value. Every pound of your investment goes directly into practical execution — driving organic visibility, qualified leads, and high-converting search performance without unnecessary overheads.
                </p>
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Button asChild size="lg" className="shadow-md">
                  <Link to="/ContactPage">
                    Book a Free Consultation
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg">
                  <Link to="/CaseStudiesPage">
                    Explore My Case Studies
                  </Link>
                </Button>
              </div>
            </motion.div>

            {/* Budget Prioritisation Roadmap Card */}
            <motion.div variants={itemVariants} className="lg:col-span-5 w-full">
              <div className="relative rounded-2xl bg-card border border-border p-6 sm:p-8 shadow-lg">
                <div className="flex items-center justify-between pb-6">
                  <div>
                    <span className="text-xs font-semibold text-primary uppercase tracking-wider">Strategic Roadmap</span>
                    <h3 className="text-xl font-bold text-card-foreground font-serif mt-1">
                      Prioritising High-Impact SEO
                    </h3>
                  </div>
                  <div className="rounded-full bg-primary/10 p-2.5 text-primary">
                    <TrendingUp className="size-5" />
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Phase 1: High-Impact Foundations */}
                  <div className="rounded-xl bg-background/60 p-4 border border-border/70 space-y-3">
                    <div className="flex items-center gap-2 text-foreground font-semibold text-sm">
                      <Target className="size-4 shrink-0 text-primary" />
                      <span>Phase 1: Foundations & Commercial Pages</span>
                    </div>
                    <ul className="space-y-2 text-xs sm:text-sm text-muted-foreground">
                      {foundationPriorities.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-2">
                          <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                          <span>{item.text}</span>
                        </li>)}
                    </ul>
                  </div>

                  {/* Phase 2: Scope Expansion & Scaling */}
                  <div className="rounded-xl bg-primary/5 p-4 border border-primary/20 space-y-3">
                    <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                      <Layers className="size-4 shrink-0" />
                      <span>Phase 2: Expansion & Scaling as You Grow</span>
                    </div>
                    <ul className="space-y-2 text-xs sm:text-sm text-foreground">
                      {scalingPriorities.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-2">
                          <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                          <span>{item.text}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5 font-medium text-foreground">
                    <ShieldCheck className="size-4 text-primary" /> Start lean, compound steadily
                  </span>
                  <span>Scale at your pace</span>
                </div>
              </div>
            </motion.div>
          </div>

          <Separator className="my-8" />

          {/* Key Value Cards */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border border-border shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
              <CardContent className="pt-6 space-y-3">
                <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <UserCheck className="size-5" />
                </div>
                <h4 className="text-lg font-bold font-serif text-card-foreground">Hands-On Delivery</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Every audit, consultation, and strategic recommendation is delivered personally by me, ensuring maximum efficiency, senior-level precision, and strong return on your SEO investment.
                </p>
              </CardContent>
            </Card>

            <Card className="border border-border shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
              <CardContent className="pt-6 space-y-3">
                <div className="size-10 rounded-lg bg-accent/20 flex items-center justify-center text-primary">
                  <Sparkles className="size-5 text-primary" />
                </div>
                <h4 className="text-lg font-bold font-serif text-card-foreground">Commercially Focused</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  We prioritize search queries that drive actual quote requests, phone calls, and revenue instead of inflated vanity clicks.
                </p>
              </CardContent>
            </Card>

            <Card className="border border-border shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
              <CardContent className="pt-6 space-y-3">
                <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-secondary-foreground">
                  <ShieldCheck className="size-5 text-primary" />
                </div>
                <h4 className="text-lg font-bold font-serif text-card-foreground">Honest Measurement</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Transparent dashboards and straightforward monthly summaries so you always understand where every pound is invested.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}